import api from './api';

/**
 * Upload Service - Handles all file upload related API calls
 */
const uploadService = {
  /**
   * Remove an image file
   * @param {string} file - File name to remove
   * @param {string} path - File path location
   * @returns {Promise} Resolves to removal result
   */
  removeImage: async (file, path) => {
    const response = await api.post('api/upload/remove-image', { file, path });
    return response.data;
  },

  /**
   * Upload a song file for distribution
   * @param {string} filename - Name for the file
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise} Resolves to upload result
   */
  uploadSong: async (filename, file, onProgress) => {
    const formData = new FormData();
    formData.append('filename', filename);
    formData.append('file', file);

    const response = await api.post('api/upload-distribution-song', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });

    // Clean up to prevent memory leaks
    formData.delete('file');

    return response.data;
  },

  /**
   * Upload a mastering file
   * @param {string} filename - Name for the file
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback function
   * @param {string} _id - ID of associated record
   * @returns {Promise} Resolves to upload result
   */
  uploadMastering: async (filename, file, onProgress, _id) => {
    const formData = new FormData();
    formData.append('filename', filename);
    formData.append('file', file);
    formData.append('_id', _id);

    const response = await api.post('api/upload-mastering', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });

    // Clean up to prevent memory leaks
    formData.delete('file');

    return response.data;
  },

  /**
   * Upload a license file
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise} Resolves to upload result
   */
  uploadLicense: async (file, onProgress) => {
    const formData = new FormData();
    const fileName = `${new Date().getTime()}.${file.name.split('.').pop()}`;

    formData.append('filename', fileName);
    formData.append('file', file);
    formData.append('path', 'distro-licenses');

    const response = await api.post('api/upload-license', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });

    return response.data;
  },

  /**
   * Remove a license file
   * @param {string} fileName - Name of file to remove
   * @returns {Promise} Resolves to removal result
   */
  removeLicense: async (fileName) => {
    const fileNameOnly = fileName.split('/').pop();
    const formData = new FormData();

    formData.append('fileName', fileNameOnly);
    formData.append('path', 'distro-licenses');

    const response = await api.post('api/remove-license', formData);
    return response.data;
  },

  /**
   * Upload an image file
   * @param {File} file - File object to upload
   * @param {string} path - Path where to store the image
   * @param {string} filename - Optional custom filename
   * @param {string} target - Optional target entity
   * @param {string} id - Optional ID associated with the image
   * @returns {Promise} Resolves to upload result
   */
  uploadImage: async (file, path, filename, target, id) => {
    if (!file) {
      return null;
    }

    const formData = new FormData();
    const customFilename = filename || `${new Date().getTime()}.jpg`;

    formData.append('filename', customFilename);
    formData.append('file', file);

    if (target) {
      formData.append('target', target);
    }

    if (id) {
      formData.append('id', id);
    }

    if (path) {
      formData.append('path', path);
    }

    const response = await api.post('api/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Remove a song file
   * @param {string} file - File name to remove
   * @param {string} path - File path location
   * @returns {Promise} Resolves to removal result
   */
  removeSong: async (file, path) => {
    const response = await api.post('api/upload/remove-song-file', { file, path });
    return response.data;
  },

  /**
   * Upload a file for dynamic forms (form-files path)
   * @param {File} file - File object to upload
   * @param {Function} onProgress - Progress callback function
   * @returns {Promise} Resolves to upload result
   */
  uploadFormFile: function (file, onProgress) {
    const formData = new FormData();
    const fileName = `${new Date().getTime()}.${file.name.split('.').pop()}`;
    formData.append('filename', fileName);
    formData.append('file', file);
    formData.append('path', 'form-files');

    return api.post('api/upload-license', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });
  },
};

export default uploadService;